import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core'
import { CommonModule, CurrencyPipe, DatePipe } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core'
import { MatTooltip, MatTooltipModule } from '@angular/material/tooltip'
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { CheckboxToggleComponent } from '@components/checkbox-side/checkbox-toggle.component';
//DISABLED: should the component have dependency with a project service?
// import { DateFormatService } from '../../services/date-format.service';

/**
 * Tipo de formato para las columnas
 */
export type ColumnFormat = 'text' | 'currency' | 'date' | 'number' | 'boolean' | 'points' | 'translated_material' | 'action' | 'toggle' | 'link';

/**
 * Configuración de una columna
 */
export interface ColumnConfig {
  key: string;
  header?: string;
  visible?: boolean;
  format?: ColumnFormat;
  formatOptions?: any; // Opciones específicas para el formato (ej: formato de fecha, símbolo de moneda)
}

@Component({
    selector: 'app-table',
    standalone: true,
    imports: [CommonModule, TranslateModule, CurrencyPipe, DatePipe, MatTooltip, MatTooltipModule, CheckboxToggleComponent],
    templateUrl: './table.component.html',
    styleUrl: './table.component.scss',
})
export class TableComponent implements OnInit{
    /**
     * Datos a mostrar en la tabla
     */
    @Input() data: any[] = []

    /**
     * Configuración de columnas
     */
    @Input() columns: ColumnConfig[] = []

    /**
     * Habilitar selección de filas con checkbox
     */
    @Input() selectable: boolean = false

    /**
     * Elementos seleccionados actualmente
     */
    @Input() selectedItems: any[] = []

    /**
     * Mensaje a mostrar cuando no hay datos
     */
    @Input() noDataMessage: string = 'lead_actions.extras.no_results'

    @Input() borders: boolean = true

    @Input() hoverOnDisabled = false
    @Input() tooltipOnDisabled: string;

    @Input() toggleDisabledTooltip: string = '';
    @Input() showEmployeePhoneTooltip: boolean = false;
    @Input() employeePhonePropertyKey: string = 'phone';

    @Input() rowActions: { label?: string; action: string; iconSvg?: string; visible?: (item: any) => boolean }[] = [];

    /**
     * Habilitar overflow-x en la tabla
     */
    @Input() overFlowXEnabled: boolean = false;

    /**
     * Evento emitido cuando cambia la selección
     */
    @Output() selectionChanged = new EventEmitter<any[]>()

    /**
     * Evento emitido cuando se hace clic en una acción de fila
     */
    @Output() actionClicked = new EventEmitter<any>();

    @Output() toggleChanged = new EventEmitter<{ item: any; key: string; value: boolean }>();

    language: string;

    sanitizedActions: { label?: string; action: string; iconSvg?: SafeHtml; visible?: (item: any) => boolean }[] = [];

    // constructor(private dateFormatService: DateFormatService) {}
    constructor(private translate: TranslateService, private sanitizer: DomSanitizer) {}

    ngOnInit() {
        this.language = this.translate.currentLang;

        this.sanitizedActions = this.rowActions.map(action => ({
            ...action,
            iconSvg: action.iconSvg ? this.sanitizer.bypassSecurityTrustHtml(action.iconSvg) : undefined //NOSONAR
        }));
    }

    trackByRow(index: number, item: any) {
        return item?.id ?? item?.name ?? index;
    }

    trackByCol(index: number, col: ColumnConfig) {
        return col.key ?? index;
    }

    /**
     * Verifica si un elemento está seleccionado
     */
    isSelected(item: any): boolean {
        if (!this.selectedItems || !this.selectedItems.length) return false

        // Si los elementos tienen un ID único, podemos comparar por ID
        if (item.id) {
            return this.selectedItems.some((selected) => selected.id === item.id)
        }

        // Si no tienen ID, comparamos el objeto completo (menos eficiente)
        return this.selectedItems.includes(item)
    }

    /**
     * Alterna la selección de un elemento
     */
    toggleSelection(item: any): void {
        if (this.isSelected(item)) {
            // Eliminar de la selección
            this.selectedItems = this.selectedItems.filter((selected) => {
                if (item.id) {
                    return selected.id !== item.id
                }
                return selected !== item
            })
        } else {
            // Añadir a la selección
            this.selectedItems = [...this.selectedItems, item]
        }

        // Emitir el evento con los elementos seleccionados
        this.selectionChanged.emit(this.selectedItems)
    }

    /**
     * Obtiene las columnas visibles
     */
    getVisibleColumns(): ColumnConfig[] {
        // Si no se proporcionan columnas, generarlas automáticamente del primer elemento
        if (!this.columns || this.columns.length === 0) {
            if (this.data && this.data.length > 0) {
                return Object.keys(this.data[0]).map((key) => ({ key, header: key }))
            }
            return []
        }

        // Filtrar columnas visibles y asegurar que todas tengan un header
        return this.columns
            .filter((col) => col.visible !== false)
            .map((col) => ({
                key: col.key,
                header: col.header || col.key,
                format: col.format || 'text',
                formatOptions: col.formatOptions,
            }))
    }

    /**
     * Obtiene el valor de una propiedad de un objeto, incluso si es una propiedad anidada
     * Ejemplo: "user.address.street" obtendrá obj.user.address.street
     */
    getPropertyValue(obj: any, key: string): any {
        if (!obj || !key) return ''

        const props = key.split('.')
        let value = obj

        for (const prop of props) {
            if (value === null || value === undefined) return ''
            value = value[prop]
        }

        return value
    }

    /**
     * Formatea un valor según el tipo de columna
     */
    formatValue(value: any, format: ColumnFormat = 'text', options?: any, fullValue?: any): string {
        if (value === null || value === undefined || value === '') return '-'

        switch (format) {
            case 'currency':
                const currency = options?.currency || fullValue?.currency || 'USD'
                const symbol = options?.symbol || 'symbol'
                const digits = options?.digits || '1.2-2'
                return new CurrencyPipe(this.language).transform(value, currency, symbol, digits) || '-'

            case 'date':
                return new DatePipe(this.language).transform(value, 'mediumDate') || '-'
            //DISABLED: should the component have dependency with a project service?
            // case 'date':
            // Usar nuestro servicio de fechas en lugar del DatePipe
            // const includeTime = options?.includeTime || false;
            // return this.dateFormatService.formatDate(value, includeTime) || '-';

            case 'number':
                const decimals = options?.decimals || '1.0-2'
                return (
                    new Intl.NumberFormat(this.language, {
                        minimumFractionDigits: parseInt(decimals.split('-')[0].split('.')[1]),
                        maximumFractionDigits: parseInt(decimals.split('-')[1]),
                    }).format(value) || '-'
                )

            case 'boolean':
                return value ? options?.trueText || 'Yes' : options?.falseText || 'No'
            case 'translated_material':
                const translations = <{name: string; locale: string;}[]>value
                const locale = this.language
                const foundTranslation = translations.find((translation) => translation.locale === locale)
                if(foundTranslation) {
                    return foundTranslation.name;
                } else {
                    return translations.find((translation) => translation.locale === 'z2' || translation.locale === 'en')?.name || '-'
                }
            case 'text':
            case 'points':
            default:
                return String(value) || '-'
        }
    }

    /**
     * Maneja el clic en una acción de fila
     */
    onActionClick(item: any, action: string): void {
        this.actionClicked.emit({ action, item });
    }

    onToggleChanged(item: any, key: string, value: boolean) {
        this.toggleChanged.emit({ item, key, value });
    }

    getVisibleActions(item: any) {
        return this.sanitizedActions.filter(a => !a.visible || a.visible(item));
    }

    isValueDisabled(item: any) {
        if(item && item.isSelectable !== undefined){
            return !item.isSelectable
        }

        return false;
    }

    isToggleDisabled(item: any, key: string) {
        if(key === 'we_access')
            return true;
        if(!item.can_update_we_access)
            return true;
        return false;
    }

    shouldShowEmployeePhoneTooltip(columnIndex: number, item: any): boolean {
        if (!this.showEmployeePhoneTooltip || columnIndex !== 0 || !item) return false;

        const phone = this.getEmployeePhone(item);
        return !!phone;
    }

    getEmployeePhone(item: any): string {
        if (!item || !this.employeePhonePropertyKey) return '';
        const phone = this.getPropertyValue(item, this.employeePhonePropertyKey);
        return phone ? String(phone) : '';
    }
}
